import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import PWAPrompt from './components/PWAPrompt.jsx';
import { useAuthStore }    from './store/authStore.js';
import { useSessionStore } from './store/sessionStore.js';
import { stripeTerminal }  from './native/stripeTerminal.js';
import { thermalPrinter }  from './native/thermalPrinter.js';
import { usePrinterStore } from './store/printerStore.js';

import LoginScreen       from './screens/LoginScreen.jsx';
import OpenSessionScreen from './screens/OpenSessionScreen.jsx';
import CatalogScreen     from './screens/CatalogScreen.jsx';
import CheckoutScreen    from './screens/CheckoutScreen.jsx';
import ReceiptScreen     from './screens/ReceiptScreen.jsx';
import CloseSessionScreen  from './screens/CloseSessionScreen.jsx';
import CorteScreen         from './screens/CorteScreen.jsx';
import PrinterSetupScreen  from './screens/PrinterSetupScreen.jsx';
import RefundScreen        from './screens/RefundScreen.jsx';
import ReaderPickerScreen  from './screens/ReaderPickerScreen.jsx';
import SalesHistoryScreen     from './screens/SalesHistoryScreen.jsx';
import SessionsHistoryScreen  from './screens/SessionsHistoryScreen.jsx';
import TabsScreen             from './screens/TabsScreen.jsx';

function useHydrated() {
  const [hydrated, setHydrated] = useState(
    () => useAuthStore.persist.hasHydrated() && useSessionStore.persist.hasHydrated()
  );
  useEffect(() => {
    if (useAuthStore.persist.hasHydrated() && useSessionStore.persist.hasHydrated()) {
      setHydrated(true);
      return;
    }
    let authDone = useAuthStore.persist.hasHydrated();
    let sessDone = useSessionStore.persist.hasHydrated();
    const check = () => { if (authDone && sessDone) setHydrated(true); };
    const u1 = useAuthStore.persist.onFinishHydration(() => { authDone = true; check(); });
    const u2 = useSessionStore.persist.onFinishHydration(() => { sessDone = true; check(); });
    return () => { u1(); u2(); };
  }, []);
  return hydrated;
}

function PrivateRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hydrated = useHydrated();
  if (!hydrated) return null;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function SessionRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const session         = useSessionStore((s) => s.session);
  const hydrated = useHydrated();
  if (!hydrated) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!session)         return <Navigate to="/open-session" replace />;
  return children;
}

export default function App() {
  useEffect(() => {
    stripeTerminal.initialize().catch((err) =>
      console.warn('[StripeTerminal] init error:', err)
    );

    // Reconecta la impresora guardada sin pedir el selector, para que
    // el auto-imprimir del checkout funcione tras recargar la app.
    const { deviceId, transport } = usePrinterStore.getState();
    thermalPrinter.init()
      .then(() => thermalPrinter.autoReconnect(deviceId, transport))
      .catch((err) => console.warn('[Printer] autoReconnect:', err));
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />

        <Route
          path="/open-session"
          element={
            <PrivateRoute>
              <OpenSessionScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/"
          element={
            <SessionRoute>
              <CatalogScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/checkout"
          element={
            <SessionRoute>
              <CheckoutScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/receipt"
          element={
            <SessionRoute>
              <ReceiptScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/close-session"
          element={
            <PrivateRoute>
              <CloseSessionScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/corte"
          element={
            <PrivateRoute>
              <CorteScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/refund"
          element={
            <PrivateRoute>
              <RefundScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/printer-setup"
          element={
            <PrivateRoute>
              <PrinterSetupScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/reader-picker"
          element={
            <PrivateRoute>
              <ReaderPickerScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/sales-history"
          element={
            <SessionRoute>
              <SalesHistoryScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/sessions-history"
          element={
            <SessionRoute>
              <SessionsHistoryScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/tabs"
          element={
            <SessionRoute>
              <TabsScreen />
            </SessionRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <PWAPrompt />
    </BrowserRouter>
  );
}
